/**
 * Optimized Cart Drawer JavaScript
 * Extends existing CartDrawer functionality with conversion optimization features
 */

class OptimizedCartDrawer extends HTMLElement {
  constructor() {
    super();

    this.isUpdating = false;
    this.processingClicks = new Set(); // Track which line indices are being processed
    this._refreshInFlight = false;
    this._openedAsSkeleton = false;

    // TODO: remove after 2026-05-12 if stable
    // Kill-switch: ?cart_v2=0 reverts to legacy open()-self-refreshes behavior.
    this._cartV2Enabled = (() => {
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('cart_v2') === '0') return false;
      } catch {
        /* SSR / older browsers without URLSearchParams */
      }
      return true;
    })();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    // Add click handler to the entire cart-drawer element
    this.addEventListener('click', (e) => {
      // Check if the click was on the overlay
      if (e.target.id === 'CartDrawer-Overlay') {
        this.close();
      }
    });

    this.setHeaderCartIconAccessibility();

    // Use event delegation for quantity buttons (like original cart drawer)
    this.buttonClickHandler = this.handleButtonClick.bind(this);
    this.addEventListener('click', this.buttonClickHandler, true); // Use capture phase

    // Use event delegation for form submissions
    this.addEventListener('submit', this.handleFormSubmit.bind(this), true);

    // Initialize cart note
    this.initializeCartNote();
  }

  disconnectedCallback() {
    if (this.errorHideTimeout) {
      clearTimeout(this.errorHideTimeout);
      this.errorHideTimeout = null;
    }
  }

  /**
   * Handle form submissions using event delegation
   */
  async handleFormSubmit(e) {
    const form = e.target;

    // Check if this is the cross-sell form
    if (form.dataset.type === 'add-to-cart-form') {
      e.preventDefault();
      e.stopPropagation();

      const submitButton = form.querySelector('button[type="submit"]');
      const originalText = submitButton.textContent;

      // Update button state
      submitButton.textContent = 'Adding...';
      submitButton.disabled = true;

      try {
        const formData = new FormData(form);

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Failed to add item');
        }

        // Refresh cart drawer
        await this.refreshHTML();

        // Show success state
        submitButton.textContent = 'Added!';
        setTimeout(() => {
          submitButton.textContent = originalText;
          submitButton.disabled = false;
        }, 2000);

      } catch (error) {
        console.error('Error adding product to cart:', error);

        // Reset button state
        submitButton.textContent = originalText;
        submitButton.disabled = false;

        // Determine error type and show user feedback
        const isNetworkError = !navigator.onLine || error.message.includes('fetch');
        const errorMessage = isNetworkError
          ? 'Connection lost. Unable to add item.'
          : 'Unable to add item to cart. Please try again.';

        this.showError(errorMessage, {
          onRetry: () => {
            // Re-trigger form submission
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          },
          retryLabel: 'Try Again'
        });
      }
    }
  }

  /**
   * Handle button clicks using event delegation (from original cart drawer)
   */
  handleButtonClick(e) {
    // Helios pack nudge: "Add my free shirt" / "Add another shirt"
    const packAddButton = e.target.closest('[data-wr-pack-add]');
    if (packAddButton) {
      e.preventDefault();
      e.stopPropagation();
      this.addPackUnit(packAddButton);
      return;
    }

    // Check if we clicked on a quantity or remove button
    const button = e.target.closest('button.quantity__button, a.cart-item__remove-link');
    if (!button) return;

    // Prevent default and stop propagation
    e.preventDefault();
    e.stopPropagation();

    // Find the cart item row
    const row = e.target.closest('tr.cart-item');
    if (!row) {
      console.error('[OptimizedCartDrawer] Could not find cart item row');
      return;
    }

    // Get the line index (1-based) from the row — this targets a specific
    // line item even when multiple items share the same variant ID (bundles).
    const lineIndex = row.dataset.lineIndex;
    if (!lineIndex) {
      console.error('[OptimizedCartDrawer] Could not find line index on row');
      return;
    }

    // Check if we're already processing this line
    if (this.processingClicks.has(lineIndex)) {
      return;
    }

    // Find the quantity input in this row
    const qtyInput = row.querySelector('input.quantity__input');
    if (!qtyInput) {
      console.error('[OptimizedCartDrawer] Could not find quantity input');
      return;
    }

    // Get current quantity - this is the actual current value
    let currentQty = parseInt(qtyInput.value, 10);
    if (isNaN(currentQty)) currentQty = 0;

    let newQty = currentQty;

    // Determine action based on button type
    if (button.classList.contains('quantity__button')) {
      const action = button.getAttribute('name');
      if (action === 'plus') {
        newQty = currentQty + 1;
      } else if (action === 'minus') {
        newQty = Math.max(0, currentQty - 1);
      }
    } else if (button.classList.contains('cart-item__remove-link')) {
      // Remove button
      newQty = 0;
    }

    // Only update if quantity actually changed
    if (newQty !== currentQty) {
      // Mark this line as being processed
      this.processingClicks.add(lineIndex);

      // Update the input value visually
      qtyInput.value = newQty;

      // Disable this row's buttons
      this.setRowButtonsDisabled(row, true);

      // Update the cart using line index
      this.updateCartQuantity(lineIndex, newQty);
    }
  }

  /**
   * Add one more unit of a Helios pack shirt from the cart nudge
   * (snippets/wr-pack-cart-nudge.liquid). Buy X Get Y discounts do not add the
   * free unit themselves, so the shopper adds it here.
   */
  async addPackUnit(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.getAttribute('aria-busy') === 'true') return;

    const nudge = button.closest('[data-wr-pack-nudge]');
    const errorElement = nudge ? nudge.querySelector('[data-wr-pack-add-error]') : null;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || 'Unable to add the shirt.');
      }
      await this.refreshHTML();
    } catch (error) {
      console.error('[OptimizedCartDrawer] Pack add failed:', error);
      if (button.isConnected) {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
      if (errorElement && errorElement.isConnected) {
        errorElement.textContent = error.message || 'Unable to add the shirt. Please try again.';
        errorElement.hidden = false;
      }
    }
  }

  /**
   * Set row buttons disabled state
   */
  setRowButtonsDisabled(row, disabled) {
    row.querySelectorAll('button.quantity__button, a.cart-item__remove-link').forEach(btn => {
      if (disabled) {
        btn.setAttribute('disabled', 'disabled');
        btn.style.pointerEvents = 'none';
        btn.style.opacity = '0.5';
      } else {
        btn.removeAttribute('disabled');
        btn.style.pointerEvents = '';
        btn.style.opacity = '';
      }
    });
  }

  /**
   * Initialize cart note functionality
   */
  initializeCartNote() {
    const cartNote = this.querySelector('#CartDrawer-Note');
    let noteTimeout;

    if (cartNote) {
      cartNote.addEventListener('input', () => {
        clearTimeout(noteTimeout);
        noteTimeout = setTimeout(() => {
          this.updateCartNote(cartNote.value);
        }, 500);
      });
    }
  }

  /**
   * Update cart note
   */
  async updateCartNote(note) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ note })
      });
    } catch (error) {
      console.error('Error updating cart note:', error);

      // Show error without retry (note updates happen automatically on input)
      this.showError('Unable to save order note. Please try again.', {
        autoHideDelay: 5000
      });
    }
  }

  /**
   * Update cart quantity via API
   */
  async updateCartQuantity(lineIndex, quantity) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: parseInt(lineIndex, 10),
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      await this.refreshDrawer();

    } catch (error) {
      console.error('Error updating cart:', error);

      // Determine if this is a network error
      const isNetworkError = !navigator.onLine || error.message.includes('fetch');
      const errorMessage = isNetworkError
        ? 'Connection lost. Check your internet and try again.'
        : 'Unable to update cart. Please try again.';

      // Show error with retry option
      this.showError(errorMessage, {
        onRetry: () => {
          this.updateCartQuantity(lineIndex, quantity);
        },
        retryLabel: 'Retry'
      });

      // Re-enable the row on error
      const row = this.querySelector(`tr[data-line-index="${lineIndex}"]`);
      if (row) {
        this.setRowButtonsDisabled(row, false);
      }
    } finally {
      // Clear the processing flag for this line
      this.processingClicks.delete(lineIndex);
      this.isUpdating = false;
    }
  }

  /**
   * Refresh the cart drawer content. Alias for refreshHTML — preserved so
   * the internal /cart/change.js path (line-item updates) stays explicit.
   */
  refreshDrawer() {
    return this.refreshHTML();
  }

  /**
   * Update header cart count from a parsed drawer document. Eliminates the
   * separate /cart.js round trip — cart count is emitted in the drawer view
   * via snippets/cart-drawer-optimized.liquid as <span data-cart-count>.
   */
  updateHeaderCartCountFromHTML(parsedDoc) {
    const newCountEl = parsedDoc.querySelector('[data-cart-count], .cart-count-bubble');
    if (!newCountEl) return;
    const raw = newCountEl.textContent.trim();
    const newCount = parseInt(raw, 10) || 0;
    // Skip elements marked [hidden] (e.g. the source-of-truth span inside the
    // drawer itself) — writing inline display would beat the hidden attribute
    // and render a stray "1" inside the drawer header.
    document.querySelectorAll('.cart-count:not([hidden]), .cart-count-bubble:not([hidden]), [data-cart-count]:not([hidden])').forEach(el => {
      el.textContent = newCount;
      el.style.display = newCount > 0 ? 'block' : 'none';
    });
    // Update is-empty class on the drawer
    if (newCount > 0) {
      this.classList.remove('is-empty');
    } else {
      this.classList.add('is-empty');
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHTML(str) {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  /**
   * Show error message with optional retry functionality
   * @param {string} message - The error message to display
   * @param {Object} options - Configuration options
   * @param {Function} options.onRetry - Callback function for retry button
   * @param {string} options.retryLabel - Label for retry button (default: 'Try Again')
   * @param {number} options.autoHideDelay - Auto-hide delay in ms (default: 8000, 0 to disable)
   */
  showError(message, options = {}) {
    const { onRetry = null, retryLabel = 'Try Again', autoHideDelay = 8000 } = options;
    const errorContainer = this.querySelector('#CartDrawer-CartErrors');

    if (!errorContainer) {
      console.error('[CartDrawer] Error container not found');
      return;
    }

    // Clear any existing auto-hide timer
    if (this.errorHideTimeout) {
      clearTimeout(this.errorHideTimeout);
    }

    // Build error HTML with accessibility support
    const errorHTML = `
      <div class="cart-error__content">
        <svg class="cart-error__icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
        </svg>
        <span class="cart-error__message">${this.escapeHTML(message)}</span>
      </div>
      ${onRetry ? `<button type="button" class="cart-error__retry">${this.escapeHTML(retryLabel)}</button>` : ''}
      <button type="button" class="cart-error__dismiss" aria-label="Dismiss error">
        <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;

    errorContainer.innerHTML = errorHTML;
    errorContainer.classList.add('is-visible');

    // Set ARIA attributes for screen reader announcement
    errorContainer.setAttribute('role', 'alert');
    errorContainer.setAttribute('aria-live', 'assertive');
    errorContainer.setAttribute('aria-atomic', 'true');

    // Attach retry button handler
    if (onRetry) {
      const retryButton = errorContainer.querySelector('.cart-error__retry');
      if (retryButton) {
        retryButton.addEventListener('click', () => {
          this.hideError();
          onRetry();
        }, { once: true });
      }
    }

    // Attach dismiss button handler
    const dismissButton = errorContainer.querySelector('.cart-error__dismiss');
    if (dismissButton) {
      dismissButton.addEventListener('click', () => {
        this.hideError();
      }, { once: true });
    }

    // Auto-hide after delay (if not disabled)
    if (autoHideDelay > 0) {
      this.errorHideTimeout = setTimeout(() => {
        this.hideError();
      }, autoHideDelay);
    }
  }

  /**
   * Hide error message
   */
  hideError() {
    const errorContainer = this.querySelector('#CartDrawer-CartErrors');
    if (errorContainer) {
      errorContainer.classList.remove('is-visible');
      errorContainer.innerHTML = '';

      // Clear any pending auto-hide timer
      if (this.errorHideTimeout) {
        clearTimeout(this.errorHideTimeout);
        this.errorHideTimeout = null;
      }
    }
  }

  /**
   * Enable interactions after update
   */
  enableInteractions() {
    const buttons = this.querySelectorAll('button, input');
    buttons.forEach(button => {
      button.disabled = false;
    });
  }

  /**
   * Refresh HTML content. Single job: fetch cart?view=drawer, swap drawer
   * inner HTML, update header count from same response. Does NOT open or
   * close the drawer. Caller is responsible for paint state.
   *
   * Guards against rapid double-tap by serializing in-flight refreshes.
   */
  async refreshHTML() {
    if (this._refreshInFlight) return this._refreshInFlight;

    const promise = (async () => {
      try {
        const cartUrl = window.Shopify?.routes?.root
          ? window.Shopify.routes.root + 'cart?view=drawer'
          : '/cart?view=drawer';
        const response = await fetch(cartUrl, {
          method: 'GET',
          headers: {
            'Accept': 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const html = await response.text();
        const parser = new DOMParser();
        const parsedDoc = parser.parseFromString(html, 'text/html');
        const newInner = parsedDoc.querySelector('.drawer__inner');

        if (newInner) {
          const currentInner = this.querySelector('.drawer__inner');
          if (currentInner) {
            currentInner.innerHTML = newInner.innerHTML;
            currentInner.style.removeProperty('display');
            this.dataset.populated = 'true';
            this.classList.remove('is-loading');
            this._openedAsSkeleton = false;

            // Reinitialize components after refresh (event handlers stay due to delegation)
            this.initializeCartNote();

            // Update cart count from same response (no separate /cart.js fetch)
            this.updateHeaderCartCountFromHTML(parsedDoc);

            // Refresh Judge.me widgets
            if (window.judgeme && window.judgeme.widgets) {
              window.judgeme.widgets.init();
            }

            // Re-arm Dawn's focus trap: open() trapped focus before this swap
            // replaced the drawer's first/last focusable nodes, so without this
            // Tab / Shift+Tab escapes the drawer on every open.
            if (this.classList.contains('active') && typeof trapFocus === 'function') {
              const active = document.activeElement;
              trapFocus(currentInner, active && active.isConnected && currentInner.contains(active) ? active : currentInner);
            }
          }
        }
      } catch (error) {
        console.error('[OptimizedCartDrawer] Error refreshing cart:', error);
        this.classList.remove('is-loading');

        // If we opened to a skeleton and the refresh failed, close to avoid
        // leaving the user staring at a stuck skeleton.
        if (this._openedAsSkeleton) {
          this._openedAsSkeleton = false;
          this.close();
        }

        const isNetworkError = !navigator.onLine || error.message.includes('fetch');
        const errorMessage = isNetworkError
          ? 'Connection lost. Unable to update cart display.'
          : 'Unable to update cart display. Please try again.';

        this.showError(errorMessage, {
          onRetry: () => {
            this.refreshHTML();
          },
          retryLabel: 'Refresh'
        });
      } finally {
        this._refreshInFlight = false;
      }
    })();

    this._refreshInFlight = promise;
    return promise;
  }

  /**
   * Open the cart drawer NOW. Single job: paint visible state.
   * - If drawer has populated content (return-visit): show that content.
   * - If not populated yet: render a skeleton placeholder.
   * Never fetches. Caller must call refreshHTML() separately.
   *
   * Legacy behavior (open() self-refreshes) kept behind ?cart_v2=0 kill-switch.
   * TODO: remove kill-switch after 2026-05-12 if stable.
   */
  open(triggeredBy) {
    // Remember what opened the drawer so close() can return focus there
    // (WCAG 2.4.3). Only capture on the transition from closed -> open: the
    // header icon fires open() twice per click (inline onclick + listener),
    // and the second call would otherwise record an element inside the drawer.
    if (!this.classList.contains('active')) {
      this._opener = this.resolveOpener(triggeredBy);
    }

    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.style.removeProperty('display');

    this.classList.add('active');
    document.body.classList.add('overflow-hidden');

    // Inject skeleton if drawer hasn't been populated yet (first-open).
    if (!this.dataset.populated && !this.querySelector('.cart-drawer__skeleton')) {
      this._openedAsSkeleton = true;
      this.classList.add('is-loading');
      const target = this.querySelector('.drawer__contents') || inner || this;
      target.insertAdjacentHTML('afterbegin',
        '<div class="cart-drawer__skeleton" aria-hidden="true">' +
        '<div class="cart-drawer__skeleton-header"></div>' +
        '<div class="cart-drawer__skeleton-row"></div>' +
        '<div class="cart-drawer__skeleton-row"></div>' +
        '<div class="cart-drawer__skeleton-footer"></div>' +
        '</div>'
      );
    }

    // Trap focus on the drawer container itself; once content swaps in,
    // focus will be re-applied via refreshHTML's component init path.
    // trapFocus() is Dawn's global from global.min.js (focusin/focusout/keydown
    // on document). Guarded because layout/theme.helios-sun-shirt.liquid loads
    // this file without global.min.js.
    if (inner && typeof trapFocus === 'function') trapFocus(inner);

    // Legacy compat: under ?cart_v2=0, open() self-refreshes (old behavior).
    if (!this._cartV2Enabled) {
      this.refreshHTML();
    }
  }

  /**
   * Close the cart drawer
   */
  close() {
    this.classList.remove('active');

    // Remove focus trap and return focus to the element that opened the drawer
    // (falls back to the header cart icon if that element left the DOM, e.g.
    // after a section re-render).
    const focusReturnTarget = this.getFocusReturnTarget();
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(focusReturnTarget);
    } else if (focusReturnTarget && typeof focusReturnTarget.focus === 'function') {
      focusReturnTarget.focus();
    }
    this._opener = null;

    // Allow body scroll
    document.body.classList.remove('overflow-hidden');

    // Reset skeleton flag so the next open() makes a fresh decision
    this._openedAsSkeleton = false;
  }

  /**
   * Pick the element focus should return to when the drawer closes.
   * Priority: explicit trigger -> element focused at open time (e.g. the
   * add-to-cart button on the renderContents/ATC path) -> #cart-icon-bubble.
   * Elements inside the drawer are ignored.
   */
  resolveOpener(triggeredBy) {
    const candidates = [triggeredBy, document.activeElement];
    const opener = candidates.find(
      (el) => el instanceof HTMLElement && typeof el.focus === 'function' && el !== document.body && !this.contains(el)
    );
    return opener || document.querySelector('#cart-icon-bubble');
  }

  getFocusReturnTarget() {
    const opener = this._opener;
    if (opener && opener.isConnected && !this.contains(opener)) return opener;
    return document.querySelector('#cart-icon-bubble');
  }

  /**
   * Set header cart icon accessibility
   */
  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        // Paint NOW (skeleton or current populated content), then refresh in background.
        this.open(cartLink);
        this.refreshHTML();
      });
    }
  }
}

// Register custom element (guard prevents duplicate registration errors)
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', OptimizedCartDrawer);
}

// Helper function to open cart drawer from external sources.
// Paints drawer immediately (skeleton or current content), refreshes in
// background. Callers do not need to await — refresh runs detached.
window.openCartDrawer = function() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) return;
  cartDrawer.open();
  if (typeof cartDrawer.refreshHTML === 'function') cartDrawer.refreshHTML();
};
